import { KeyboardEvent, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

interface TagInputProps {
  label?: string;
  helperText?: string;
  error?: string;
  placeholder?: string;
  values: string[];
  onChange: (values: string[]) => void;
  suggestions?: string[];
}

const normalizeTag = (value: string) => value.trim().replace(/\s+/g, ' ');

export const TagInput = ({
  label,
  helperText,
  error,
  placeholder = 'Type and press Enter',
  values,
  onChange,
  suggestions = [],
}: TagInputProps) => {
  const [draft, setDraft] = useState('');

  const addTag = (value: string) => {
    const normalized = normalizeTag(value);
    if (!normalized) return;
    if (values.some((item) => item.toLowerCase() === normalized.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...values, normalized]);
    setDraft('');
  };

  const removeTag = (value: string) => {
    onChange(values.filter((item) => item !== value));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(draft);
    }

    if (event.key === 'Backspace' && !draft && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const unusedSuggestions = suggestions.filter(
    (suggestion) => !values.some((item) => item.toLowerCase() === suggestion.toLowerCase())
  );

  return (
    <div className="w-full">
      {label ? <label className="form-label">{label}</label> : null}
      <div
        className={cn(
          'rounded-3xl border bg-white px-4 py-4 shadow-sm transition focus-within:border-[rgb(var(--accent-solid))] focus-within:ring-4 focus-within:ring-[rgb(var(--accent-solid))/0.12]',
          error ? 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500/10' : 'border-slate-300'
        )}
      >
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-2 rounded-full border border-[rgb(var(--accent-solid))/0.18] bg-[rgb(var(--accent-solid))/0.08] px-3 py-1 text-sm font-medium text-[rgb(var(--accent-solid))]"
            >
              {value}
              <button
                type="button"
                onClick={() => removeTag(value)}
                className="rounded-full p-0.5 text-[rgb(var(--accent-solid))] transition hover:bg-[rgb(var(--accent-solid))/0.12]"
                aria-label={`Remove ${value}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (draft.trim()) addTag(draft);
            }}
            className="min-h-10 flex-1 border-0 bg-transparent px-1 text-slate-950 outline-none placeholder:text-slate-400"
            placeholder={placeholder}
          />
        </div>

        {unusedSuggestions.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {unusedSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => addTag(suggestion)}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-white hover:text-slate-900"
              >
                {suggestion}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {helperText ? <p className="mt-2 text-sm text-slate-500">{helperText}</p> : null}
      {error ? <p className="mt-1.5 text-sm font-medium text-red-600">{error}</p> : null}
    </div>
  );
};
